// How a tenant's answers ended, for support (Phase 5, 2026-09-26; R119, §3
// of `plans/phase-5-answering-design.md`): the step limit and every other
// early end, the citations the registry refused, and the levels. By project
// and by day when asked. The agent's answers only: an answer of the old path
// has no end to count.
//
// A value of `ended_by` a later build writes shows as it is stored.

import { useState } from "react";
import { ANSWER_CONFIDENCE_LABELS } from "@gb/contracts";
import { Input } from "@/components/ui/input";
import { useProjects } from "../projects/projects-api";
import { useAnswerCounts } from "./chat-api";

/** How an answer ended, in words. The keys are `AnswerEndedBy`'s. */
const ENDED_BY_WORDS: Record<string, string> = {
  answer: "Finished",
  "step-limit": "Stopped at the step limit",
  repeating: "Stopped: a step was repeated",
  context: "Stopped: the context was nearly full",
  time: "Stopped at the time limit",
  "too-long": "Stopped: the request was over the context window",
  "max-tokens": "Cut off at the output limit",
  stopped: "Stopped by a person",
  restart: "Stopped by a server restart",
  refusal: "Refused by the model",
  error: "Failed",
};

function Tile({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-card border-[1.5px] border-border bg-surface p-[16px_18px]">
      <div className="font-display text-[2rem] font-bold text-ink">{value}</div>
      <div className="text-[0.84rem] font-semibold text-muted">{label}</div>
    </div>
  );
}

export function AnswerCounts({ tenantId }: { tenantId: string }) {
  const [projectId, setProjectId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const projects = useProjects(tenantId);
  const counts = useAnswerCounts(tenantId, {
    ...(projectId ? { projectId } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  });
  const number = (n: number) => new Intl.NumberFormat().format(n);
  const data = counts.data;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3 text-[0.78rem]">
        <label className="flex flex-col gap-1">
          <span className="font-semibold text-muted">Project</span>
          <select
            aria-label="Project"
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
            className="h-9 rounded-md border border-border bg-white px-2 text-ink"
          >
            <option value="">Every project</option>
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-semibold text-muted">From</span>
          <Input type="date" aria-label="From" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-semibold text-muted">To</span>
          <Input type="date" aria-label="To" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
      </div>

      {counts.isError && (
        <p role="alert" className="text-[0.82rem] font-semibold text-danger">
          {counts.error.message}
        </p>
      )}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Tile label="Answers of the agent" value={number(data.answers)} />
            <Tile label="At the step limit" value={number(data.endedBy["step-limit"] ?? 0)} />
            <Tile label="Unfinished, all causes" value={number(data.answers - (data.endedBy.answer ?? 0))} />
            <Tile label="Citations refused" value={number(data.refusedCitations)} />
          </div>

          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <table className="w-full text-[0.8rem]">
              <caption className="mb-1 text-left font-display text-[0.68rem] font-semibold tracking-wide text-muted uppercase">
                How each ended
              </caption>
              <tbody>
                {Object.entries(data.endedBy)
                  .sort(([, a], [, b]) => b - a)
                  .map(([endedBy, n]) => (
                    <tr key={endedBy} className="border-b border-border-inner">
                      <td className="py-1 text-ink">{ENDED_BY_WORDS[endedBy] ?? endedBy}</td>
                      <td className="py-1 text-right font-mono text-ink">{number(n)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <table className="w-full text-[0.8rem]">
              <caption className="mb-1 text-left font-display text-[0.68rem] font-semibold tracking-wide text-muted uppercase">
                Levels
              </caption>
              <tbody>
                {(["high", "partial", "needs-confirmation"] as const).map((level) => (
                  <tr key={level} className="border-b border-border-inner">
                    <td className="py-1 text-ink">{ANSWER_CONFIDENCE_LABELS[level]}</td>
                    <td className="py-1 text-right font-mono text-ink">{number(data.levels[level])}</td>
                  </tr>
                ))}
                <tr className="border-b border-border-inner">
                  <td className="py-1 text-ink">Not assessed</td>
                  <td className="py-1 text-right font-mono text-ink">{number(data.levels.none)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
